import { useEffect } from 'react';

/** Velocity is bounded so a held pointer never jumps past a target. */
export function edgeScrollDelta(pointerY: number, top: number, bottom: number): number {
  const edge = Math.min(72, Math.max(0, bottom - top) / 3);
  return pointerY < top + edge
    ? -Math.min(18, (top + edge - pointerY) / 4)
    : pointerY > bottom - edge
      ? Math.min(18, (pointerY - bottom + edge) / 4)
      : 0;
}

/** Scroll the nearest available viewport while a card is held near its edge. */
export default function useCanvasAutoScroll(active: boolean, container: React.RefObject<HTMLElement>) {
  useEffect(() => {
    if (!active || !container.current) return undefined;
    const parents: Array<HTMLElement> = [];
    let node = container.current.parentElement;
    while (node) {
      if (/(auto|scroll)/.test(getComputedStyle(node).overflowY)) parents.push(node);
      node = node.parentElement;
    }
    const root = document.scrollingElement as HTMLElement;
    if (root && !parents.includes(root)) parents.push(root);
    let pointerY: number | null = null;
    let frame = 0;
    let stopped = false;
    const track = (event: MouseEvent) => {
      pointerY = event.clientY;
    };
    const trackTouch = (event: TouchEvent) => {
      pointerY = event.touches[0]?.clientY ?? null;
    };
    const stop = () => {
      stopped = true;
      cancelAnimationFrame(frame);
    };
    const tick = () => {
      if (stopped) return;
      if (pointerY !== null) {
        for (const parent of parents) {
          const rect = parent === root ? { top: 0, bottom: window.innerHeight } : parent.getBoundingClientRect();
          const top = Math.max(0, rect.top);
          const bottom = Math.min(window.innerHeight, rect.bottom);
          const delta = edgeScrollDelta(pointerY, top, bottom);
          if (
            delta &&
            ((delta < 0 && parent.scrollTop > 0) ||
              (delta > 0 && parent.scrollTop + parent.clientHeight < parent.scrollHeight))
          ) {
            parent.scrollTop += delta;
            break;
          }
        }
      }
      frame = requestAnimationFrame(tick);
    };
    document.addEventListener('mousemove', track, true);
    document.addEventListener('mouseup', stop, true);
    document.addEventListener('touchmove', trackTouch, { capture: true, passive: true });
    document.addEventListener('touchend', stop, true);
    document.addEventListener('dragover', track, true);
    document.addEventListener('drop', stop, true);
    document.addEventListener('dragend', stop, true);
    window.addEventListener('blur', stop);
    frame = requestAnimationFrame(tick);
    return () => {
      stop();
      document.removeEventListener('mousemove', track, true);
      document.removeEventListener('mouseup', stop, true);
      document.removeEventListener('touchmove', trackTouch, true);
      document.removeEventListener('touchend', stop, true);
      document.removeEventListener('dragover', track, true);
      document.removeEventListener('drop', stop, true);
      document.removeEventListener('dragend', stop, true);
      window.removeEventListener('blur', stop);
    };
  }, [active, container]);
}
